import { useEffect, useMemo, useState } from "react";
import { useGlobalUserContext } from "../../../../hooks/useGlobalUserContext";

import { Box, CircularProgress, Divider, Typography } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { Link as RouterLink } from "react-router-dom";
import { useAlert } from "../../../../lib/alert";
import { federatedLogoutEndpoint, logout } from "../../../../lib/auth";
import eventEmitter from "../../../../lib/event";
import { WebSocketGlobalState } from "../../../../lib/websocket/websocket";
import { WebSocketConnectionState } from "../../../../model/WebSocketMessage";
import { HumanReadableTimestamp } from "../../../date";
import { UserIcon } from "../../../elements";
import IconButtonDropdownMenu from "../../../elements/IconButtonDropdownMenu";
import PATHS from "../../../navigation/_paths";

const WEBSOCKET_TOOLTIP_STATUS = {
  [WebSocketConnectionState.CONNECTED]: "Connected",
  [WebSocketConnectionState.DISCONNECTED]: "Disconnected",
  [WebSocketConnectionState.ERROR]: "Disconnected",
  [WebSocketConnectionState.CONNECTING]: "Reconnecting...",
};

const PING_CHECK_INTERNAL = 1000;
const MAX_PING_TIME = 5000;

const PING_DO_NOT_OVERRIDE = [
  WebSocketConnectionState.ERROR,
  WebSocketConnectionState.CONNECTING,
];

const AccountButton = () => {
  const { user } = useGlobalUserContext();
  const { handleRejectionWithError, warning } = useAlert();

  const [isMenuClosed, setIsMenuClosed] = useState<boolean>(true);
  const [didWarnUserOnDisconnect, setDidWarnUserOnDisconnect] =
    useState<boolean>(false);

  const handleLogout = () => {
    logout().then(() => {
      window.location.href = federatedLogoutEndpoint(window.location.origin);
    }, handleRejectionWithError("Could not complete logout"));
  };

  const [webSocketConnection, setWebSocketConnection] =
    useState<WebSocketConnectionState>(WebSocketConnectionState.DISCONNECTED);

  useEffect(() => {
    if (WebSocketGlobalState.getWebSocket()) {
      setWebSocketConnection(WebSocketConnectionState.CONNECTED);
    } else {
      setWebSocketConnection(WebSocketConnectionState.DISCONNECTED);
    }

    eventEmitter.on(
      "websocket:connectionState",
      (state: WebSocketConnectionState) => {
        setWebSocketConnection(state);
      },
    );
    return () => {
      eventEmitter.off("websocket:connectionState");
    };
  }, []);

  // check for ping on interval
  useEffect(() => {
    const pingInterval = setInterval(() => {
      const pingTime = WebSocketGlobalState.getPing();

      // do not override the status if the websocket is in error or connecting state
      if (PING_DO_NOT_OVERRIDE.includes(webSocketConnection)) return;

      // if the ping time is greater than the max ping time, set the connection to disconnected
      if (Date.now() - pingTime > MAX_PING_TIME) {
        setWebSocketConnection(WebSocketConnectionState.DISCONNECTED);

        // send a specific warning to the user after 3 times the max ping time
        if (
          Date.now() - pingTime > MAX_PING_TIME * 3 &&
          !didWarnUserOnDisconnect
        ) {
          warning("You may be disconnected!");
          setDidWarnUserOnDisconnect(true);
        }
      } else {
        if (didWarnUserOnDisconnect) setDidWarnUserOnDisconnect(false);
        setWebSocketConnection(WebSocketConnectionState.CONNECTED);
      }
    }, PING_CHECK_INTERNAL);

    return () => {
      clearInterval(pingInterval);
    };
  });

  // indicator color and value based on the ConnectionState
  const webSocketIndicatorColor = useMemo(() => {
    if (webSocketConnection == WebSocketConnectionState.CONNECTED)
      return "success";
    if (
      webSocketConnection == WebSocketConnectionState.DISCONNECTED ||
      webSocketConnection == WebSocketConnectionState.ERROR
    )
      return "error";
    return undefined;
  }, [webSocketConnection]);

  const webSocketIndicatorValue = useMemo(() => {
    if (webSocketConnection == WebSocketConnectionState.CONNECTED) return 100;
    if (webSocketConnection == WebSocketConnectionState.DISCONNECTED)
      return 100;
    if (webSocketConnection == WebSocketConnectionState.ERROR) return 100;
    return 50;
  }, [webSocketConnection]);

  if (!user?.id) {
    return;
  }
  return (
    <>
      <Box sx={{ position: "relative" }}>
        <Box
          sx={{
            "@media print": {
              display: "none", // Make all content visible for printing
            },
          }}
        >
          <IconButtonDropdownMenu
            element={
              <UserIcon
                user={user}
                title={
                  <Box display={"flex"} flexDirection="column">
                    <Typography variant="body1" fontWeight={"bold"}>
                      {user.first_name} {user.last_name}
                    </Typography>
                    <Typography variant="caption">
                      {WEBSOCKET_TOOLTIP_STATUS[webSocketConnection]}
                    </Typography>
                    {WebSocketGlobalState.getPing() && (
                      <HumanReadableTimestamp
                        timestamp={new Date(WebSocketGlobalState.getPing())}
                        format="short"
                        variant="caption"
                      />
                    )}
                  </Box>
                }
                toolTipPosition="top-end"
              />
            }
            closed={isMenuClosed}
            setClosed={setIsMenuClosed}
            size="medium"
          >
            <MenuItem>{user.email}</MenuItem>
            <Divider />
            <MenuItem
              component={RouterLink}
              to={PATHS.PROFILE.link}
              onClick={() => {
                setIsMenuClosed(true);
              }}
            >
              Profile
            </MenuItem>
            <MenuItem data-cy={"logout-btn"} onClick={handleLogout}>
              Log out
            </MenuItem>
          </IconButtonDropdownMenu>
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: -1,
            marginTop:
              webSocketConnection == WebSocketConnectionState.DISCONNECTED ||
              webSocketConnection == WebSocketConnectionState.ERROR
                ? "0px"
                : "3px",
          }}
        >
          {webSocketConnection == WebSocketConnectionState.DISCONNECTED ||
          webSocketConnection == WebSocketConnectionState.ERROR ? (
            <>
              <Box
                sx={{
                  width: "34px",
                  height: "34px",
                  borderRadius: "50%",
                  border: "2px dotted",
                  borderColor: (theme) => theme.palette.grey[500],
                }}
              ></Box>
            </>
          ) : (
            <CircularProgress
              size={34}
              color={webSocketIndicatorColor}
              value={webSocketIndicatorValue}
              variant={
                webSocketIndicatorValue == 100 ? "determinate" : "indeterminate"
              }
              disableShrink={webSocketIndicatorValue == 100 ? undefined : true}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default AccountButton;
